export enum CampType {
  sms = 'sms',
  mms = 'mms',
}

export enum AllocateCreditMode {
  add = 'add',
  subtract = 'subtract',
}

export interface AppResponse<T> {
  app_type: string
  app_version: string
  maintainence: number
  new_version: number
  force_update: number
  invalid_token: number
  refresh_token: string
  show_message: number
  is_enc: boolean
  status: boolean
  message: string
  message_type: string
  data: T
  error: Record<string, any>
  is_error: boolean
}

export interface PaymentPricingRules {
  _id?: string
  FinalPrice: number
  ActualPrice: number
  CreditStartRange: number
  CreditEndRange: number
  Deleted: boolean
  AccountID: string
  Type: string
  Region: string
}

export type CreditParam = {
  amount: number
  type: string
  cent_per_sms?: number
}

export type BuyCreditParam = {
  credit?: CreditParam
  token?: string
  card?: string
  product?: string
  recurring_time?: string
  number?: string
  price?: number
  payment_method?: string
  payment_method_type?: string
}

export type AllocateCreditParam = {
  target_user_id: string
  balance_type: CampType
  credit: number
  cents_per_sms?: number
  mode: AllocateCreditMode
}

export interface AppRequest<T> {
  action: string
  data: T
}

export enum ContactStatus {
  subscribe = 'subscribe',
  unsubscribe = 'unsubscribe',
}

export type ContactRequest = {
  page?: number | string
  limit?: number
  group_id?: string
  tag_ids?: string[]
  static_tag?: string
  exclude_tag_ids?: string[]
  static_exclude_tag?: string
  status?: ContactStatus | string | undefined
  search?: string | undefined
  sort_key?: string | undefined
  sort_value?: string | undefined
  number?: string
}

export interface Group {
  fields: string[]
  _id: string
  user: string
  name: string
  status: string
  mergeLabels: string
  createdBy: string
  totalContacts: string
}

export interface Contact {
  _id: string
  number: string
  countryCode: string
  // isBlocked: boolean
  // isBlockedByUser: boolean
  bounced: number
  addedThrough: string
  isInboxRead: boolean
}

interface Field {
  name: string
  value: string
  length: number
  isEnglish: boolean
}

export interface Tag {
  _id: string
  name: string
  type: string
  color: string
  createdAt?: string
  updatedAt?: string
}

export interface ContactGroupMap {
  _id: string
  number: string
  group: Group
  contact: Contact
  user: string
  name: string | null
  fields: Field[]
  createdAt: string
  tags: Tag[]
  cursor?: string
  creatorName: string
  createdFrom: string
  isBlocked: boolean
  isBlockedByUser: boolean
  bounced: number
}

export interface ListContactResponse {
  items: ContactGroupMap[]
  next: number
  prev: number
}

export type ContactCountRequest = {
  group_id?: string
  status?: ContactStatus | string | undefined
}

export type TagRequest = {
  page?: number | string
  limit?: number
  type?: string
  search?: string
}

export interface Tag {
  _id: string
  name: string
  industry?: string
  affiliate?: string
  color: string
  type: string
  isHidden?: boolean
  contactCount?: number
}

export interface List extends Tag {}

export interface ListTagsResponse {
  items: List[]
  next: number
  prev: number
  current: number
  total: number
  totalPages: number
}

export enum UploadHistoryStatus {
  COMPLETED = 'completed',
  FAILED = 'failed',
  QUEUED = 'queued',
  INPROGRESS = 'in-progress',
}

export type UploadHistory = {
  isRead: boolean
  _id: string
  filekey: string
  user: string
  group: string
  totalContact: string
  importedContact: string
  duplicatedContact: string
  invalidContact: string
  universalUnSub: string
  tagsCount: string
  createdBy: string
  createdAt: string
  updatedAt: string
  status: UploadHistoryStatus
  originalName?: string
  __v: number
}

export interface ListUploadHistoryResponse {
  items: UploadHistory[]
  next: number
  prev: number
  current: number
  total: number
  totalPages: number
}

export type UpsertTag = {
  tag_id?: string
  name: string
  color?: string
  type?: string
}

export type User = {
  _id: string
  name: string
  email: string
  role: string
}

export type CreditHistory = {
  _id: string
  addedBy: string
  creditAmount: number
  type: string
  user: string
  description: string
  balance: number
  status: string
  createdAt: string
  updatedAt: string
  __v: number
  addedUser: User
}

export interface ListCreditHistoryResponse {
  items: CreditHistory[]
  next: number
  prev: number
  current: number
  total: number
  totalPages: number
}

export type PurchaseHistory = {
  allowedMessages: number
  manual: boolean
  status: 'paid' | 'unpaid' | 'pending'
  paidFrom: string
  _id: string
  purchasedDate: string
  product: string
  productName: string | null
  user: string
  invoice: string
  invoicePdf: string
  type: 'sms' | 'mms'
  amount: number
  createdAt: string
  updatedAt: string
  __v: number
}

export interface ListPurchaseHistoryResponse {
  items: PurchaseHistory[]
  next: number
  prev: number
  current: number
  total: number
  totalPages: number
}

type DNPurchaseHistory = {
  status: boolean
  _id: string
  purchasedNumber: string
  email: string
  recurring: string
  ogPrice: number
  deductedPrice: number
  activationDate: string
  renewalDate: string
  stripeProductId: string
  stripeSubscrId: string
  stripeInvoiceNo: string
  stripeCustomerId: string
  stripePlanId: string
  stripeInvoicePDF: string
  stripeInvoiceURL: string
  stripeInvoiceNumber: string
  user: string
  createdAt: string
  updatedAt: string
  __v: number
  number: string
}

export interface ListDNPurchaseHistoryResponse {
  items: DNPurchaseHistory[]
  next: number
  prev: number
  current: number
  total: number
  totalPages: number
}

export type ContactClickResponse = {
  CTRCount: number
  warmContactcCount: number
  hotContactcCount: number
}

export type GetContactListResponse = AppResponse<ListContactResponse>

export type Message = {
  id: string
  user_id: string
  region: string
  bounce_number: number
  bulk_id: string
  campaign_type: string
  conversation_id: string
  country_code: string
  created_by: string
  credit_amount: number
  delivered_at: string // ISO Date String
  failed_at: string // ISO Date String
  gateway_code: string
  is_reply_stop_out: boolean
  is_scheduled: boolean
  is_user_tracking_link: boolean
  is_use_opt_out: boolean
  landing_page_click: number
  landing_page_id: string // Note: Key with '+' symbol remains a string property
  landing_page_name: string
  latest_bounce_at: string // ISO Date String
  media: any | null // Replace 'any' if a specific media type exists
  message: string
  message_cost: number
  message_length: number
  message_part: number
  raw_send_message: string
  raw_webhook_message: string
  receiver: string
  request_from: string
  response_message: {
    id: string
    message: string
    receive_at: string
    media: any | null // Replace 'any' if a specific media type exists
  }
  scheduled_at: string // ISO Date String
  send_at: string // ISO Date String
  sender: string
  sender_type: string
  subject: string
  tracking_link_click: any | null // Define appropriate type
  tracking_links: any | null // Define appropriate type
  type: string
  opt_out_id: string
  queue_id: string
  created_at: string // ISO Date String
  updated_at: string // ISO Date String
}

// Message Report
export type MessageReportParams = {
  fromDate?: string
  toDate?: string
  status?: string
  campType?: CampType
  sortKey?: string
  sortValue?: string
  page?: number
  limit?: number
  type?: string
  groupBy?: string
  isExport?: boolean
}

export interface PaginateResponse<T> {
  items: T[]
  total: number
  limit: number
  current: number
  totalPages: number
  pagingCounter: number
  hasPrevPage: boolean
  hasNextPage: boolean
  prevPage: number | null
  nextPage: number | null
}

export interface LineChartItem {
  _id: string
  count: number
}

export type Sender = {
  _id: string
  audNumber: string
  status: 'active' | 'inactive' | string // Add other possible statuses if known
  totalAmount: number
  billingStatus: 'month' | 'year' | string // Add other billing types if needed
  activationTime: string // ISO date string
  expirationTime: string // ISO date string
  isDefault: boolean
  type: 'mms' | string // Expand based on other known types
  assignedUserId: string
  createdAt: string // ISO date string
  updatedAt: string // ISO date string
}
